import React, { useEffect, useMemo, useState } from "react";

import MultipleSelectDropDown, {
  GroupedDropDownDisplayValue,
} from "../../../Common/DropDown/MultipleSelectDropDown";

import {
  getSuiteMode,
  getSuiteTestingState,
} from "../../../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import {
  getStorageConfigById,
} from "../../../../redux/reducers/StorageConfigReducer";

import {
  ETestingSlotSelection,
  ETestingType,
  TargetSelectedReliabilityTestParameter,
  TestParameter,
} from "../../../../Model/TestingParameter";
import { EFootprint } from "../../../../Model/PlateType";
import { ESuiteMode, ETestingState } from "../../../../Model/ModuleState";

import CheckBox from "../../../Buttons/CheckBox";
import SpinnerTextBox from "../../../Controls/SpinnerTextBox";
import ToolsContentButton from "../../ToolsCommon/ToolsContentButton";

import TestingApi from "../../../../api/TestingApi";

import styles from "./ReliabilityTest.module.css";
import CommonDropDown, {
  CommonDropDownDisplayValue,
} from "../../../Common/DropDown/CommonDropDown";

import { slotTargetDropdown, slotTargetValue } from "./constants";
import { EResultCode } from "../../../../Model/EResultCode";
import MtiModalIllustration from "../../../Common/MtiModalIllustration/MtiModalIllustration";
import SvgError from "../../../../Icons/ModalNotification/Error";
import { StorageConfig } from "../../../../Model/StorageConfig";
import { getModulesByType } from "../../../../redux/reducers/ModuleReducer";
import { EModuleType } from "../../../../Model/EModuleType";

interface ReliabilityFormPayload {
  iteration: number;
  plateSource: { hotel: string; slot: number };
  targetHotels: string[];
  targetSlots: number[];
  testingSlotSelection: ETestingSlotSelection[];
}

export default function ReliabilityTest() {
  const suiteMode = useTypedSelector((state) => getSuiteMode(state));
  const testingState = useTypedSelector((state) => getSuiteTestingState(state));
  const [targetSlot, setTargetSlot] = useState(slotTargetDropdown[0].value);
  const [isModalErrorOpen, setIsModalErrorOpen] = useState(false);
  const chamberModules = useTypedSelector((state) => getModulesByType(state)[EModuleType.CHAMBER]);
  const loadPortModules = useTypedSelector((state) => getModulesByType(state)[EModuleType.LOADPORT]);

  const storageConfigByIdAll = useTypedSelector((state) =>
    getStorageConfigById(state)
  );

  const storageConfigById: { [index: string]: StorageConfig } = useMemo(() => {
    let temp: { [index: string]: StorageConfig } = {};
    chamberModules.forEach((module) => {
      if (storageConfigByIdAll[module.identifier]) {
        temp[module.identifier] = storageConfigByIdAll[module.identifier];
      }
    });
    loadPortModules.forEach((module) => {
      if (storageConfigByIdAll[module.identifier]) {
        temp[module.identifier] = storageConfigByIdAll[module.identifier];
      }
    });
    return temp;

  }, [storageConfigByIdAll, chamberModules, loadPortModules]);

  const storageConfigs = useMemo(() => {
    let temp: Array<StorageConfig> = [];
    chamberModules.forEach((module) => {
      if (storageConfigByIdAll[module.identifier]) {
        temp.push(storageConfigByIdAll[module.identifier]);
      }
    });
    loadPortModules.forEach((module) => {
      if (storageConfigByIdAll[module.identifier]) {
        temp.push(storageConfigByIdAll[module.identifier]);
      }
    });
    return temp;
  }, [storageConfigByIdAll, chamberModules, loadPortModules]);

  // create dropdown for plate source hotels
  const plateSourceHotelDropdown: CommonDropDownDisplayValue<string>[] =
    useMemo(() => {
      const dropdown: CommonDropDownDisplayValue<string>[] = [];
      storageConfigs.forEach((config) => {
        dropdown.push({
          display: config.moduleIdentifier.split('-')[0],
          value: config.moduleIdentifier,
          disabled: true,
        });
        config.hotels.forEach((hotel) => {
          dropdown.push({
            display: `${config.moduleIdentifier.split('-')[0]}-${hotel.number}`,
            value: `${config.moduleIdentifier}|hotel|${hotel.number}`,
            disabled: false,
          });
        });
      });
      return dropdown;
    }, [storageConfigs]);

  const [form, setForm] = useState<ReliabilityFormPayload>({
    iteration: 1,
    plateSource: {
      hotel:
        plateSourceHotelDropdown.length > 0
          ? plateSourceHotelDropdown[0].value
          : "",
      slot: 1,
    },
    targetHotels: [],
    targetSlots: [],
    testingSlotSelection: [],
  });

  // menggunakan useEffect untuk memperbarui selectedValue jika plateSourceHotelDropdown berubah
  useEffect(() => {
    if (plateSourceHotelDropdown.length > 0) {
      setForm((prev) => ({
        ...prev,
        plateSource: {
          ...prev.plateSource,
          hotel: plateSourceHotelDropdown[1].value,
        },
      }));
    }
  }, [plateSourceHotelDropdown]);

  // create dropdown for plate source slots
  const plateSourceSlotDropdown: CommonDropDownDisplayValue<number>[] =
    useMemo(() => {
      const dropdown: CommonDropDownDisplayValue<number>[] = [];
      const identifier = form.plateSource.hotel.split("|")[0];
      const hotelNumber = parseInt(form.plateSource.hotel.split("|")[2]);
      const capacity = storageConfigById[identifier].hotels.find(
        (hotel) => hotel.number === hotelNumber
      )?.capacity;
      if (capacity)
        for (let i = 0; i < capacity; i++) {
          dropdown.push({
            display: `${i + 1}`,
            value: i + 1,
            disabled: false,
          });
        }
      return dropdown;
    }, [form.plateSource.hotel, storageConfigById]);

  // create dropdown for custom slot slot
  const customSlotDropdown: GroupedDropDownDisplayValue<number>[] =
    useMemo(() => {
      const maxSlot: number[] = [0];
      form.targetHotels.forEach((value) => {
        const identifier = value.split("|").shift()!;
        const targetHotel = parseInt(value.split("|").pop()!);
        maxSlot.push(
          storageConfigById[identifier].hotels.find(
            (hotel) => hotel.number === targetHotel
          )?.capacity!
        );
      });
      const dropdown: GroupedDropDownDisplayValue<number>[] = [
        {
          title: "",
          displayValues: [...Array(Math.max(...maxSlot))].map((_, i) => ({
            display: `${i + 1}`,
            value: i + 1,
            disabled: false,
          })),
        },
      ];
      return dropdown;
    }, [storageConfigs, form.targetHotels]);

  const groupedDisplayValues: GroupedDropDownDisplayValue<string>[] =
    [] as GroupedDropDownDisplayValue<string>[];
  for (const identifier in storageConfigById) {
    groupedDisplayValues.push({
      title: identifier,
      displayValues: storageConfigById[identifier].hotels.map((hotel) => {
        return {
          display: `${EFootprint[hotel.footprint]} ${hotel.number}`,
          value: `${identifier}|hotel|${hotel.number}`,
          disabled: false,
        };
      }),
    });
  }

  const handleCustomSlotCheckbox = (slotSelectionString: string) => {
    const slotSelectionTypeString = slotSelectionString.split("-").pop();
    if (slotSelectionTypeString) {
      const selectedSlotType = parseInt(slotSelectionTypeString);
      if (form.testingSlotSelection.includes(selectedSlotType)) {
        const index = form.testingSlotSelection.indexOf(selectedSlotType);
        let newSelectedSlotsType = [...form.testingSlotSelection];
        newSelectedSlotsType.splice(index, 1);
        setForm((prev) => ({
          ...prev,
          testingSlotSelection: newSelectedSlotsType,
        }));
      } else {
        setForm((prev) => ({
          ...prev,
          testingSlotSelection: [
            ...prev.testingSlotSelection,
            selectedSlotType,
          ],
        }));
      }
    }
  };

  const isRunEnabled = () => {
    let isEnable = false;

    if (
      form.iteration &&
      form.plateSource.hotel &&
      form.plateSource.slot &&
      form.targetHotels.length
    ) {
      isEnable = !!(
        targetSlot === slotTargetValue.allSlot ||
        (targetSlot === slotTargetValue.customSlot &&
          form.targetSlots.length) ||
        (targetSlot === slotTargetValue.selectedSlot &&
          form.testingSlotSelection.length)
      );
    }

    if (
      suiteMode === ESuiteMode.Testing &&
      testingState.reliability?.state !== ETestingState.Stopped
    )
      isEnable = false;
    return isEnable;
  };

  const stopEnabled =
    suiteMode === ESuiteMode.Testing &&
    testingState.reliability?.state === ETestingState.Started;

  const isDisabled = suiteMode !== ESuiteMode.Normal;

  const stopTest = () => {
    TestingApi.stopTesting();
  };

  const runTest = async () => {
    const plateSourceModuleIdentifier = form.plateSource.hotel
      .split("|")
      .shift()!;
    const plateSourceHotelNumber = parseInt(
      form.plateSource.hotel.split("|").pop()!
    );
    const plateSourceSlotNumber = form.plateSource.slot;
    const iteration = form.iteration;

    const selectedTarget: TargetSelectedReliabilityTestParameter[] = [];

    form.targetHotels.forEach((value) => {
      const identifier = value.split("|").shift()!;
      const targetHotel = parseInt(value.split("|").pop()!);
      let targetSlots: number[] = [];
      if (targetSlot === slotTargetValue.allSlot) {
        targetSlots = [
          ...[
            ...Array(
              storageConfigById[identifier].hotels.find(
                (hotel) => hotel.number === targetHotel
              )?.capacity
            ),
          ].map((_, i) => i + 1),
        ];
      } else if (targetSlot === slotTargetValue.customSlot) {
        const capacity = storageConfigById[identifier].hotels.find(
          (hotel) => hotel.number === targetHotel
        )?.capacity;
        targetSlots = form.targetSlots.filter((value) => value <= capacity!);
      } else {
        const capacity =
          storageConfigById[identifier].hotels.find(
            (hotel) => hotel.number === targetHotel
          )?.capacity ?? 0;
        form.testingSlotSelection.sort().forEach((value) => {
          if (value === ETestingSlotSelection.Top) {
            const topValue = 1;
            if (!targetSlots.includes(topValue)) targetSlots.push(topValue);
          } else if (value === ETestingSlotSelection.Middle) {
            const middleValue = Math.round(capacity / 2);
            if (!targetSlots.includes(middleValue))
              targetSlots.push(middleValue);
          } else if (value === ETestingSlotSelection.Bottom) {
            if (!targetSlots.includes(capacity)) targetSlots.push(capacity);
          }
        });
      }
      selectedTarget.push({
        identifier,
        targetHotel,
        targetSlots,
      });
    });

    const payload: TestParameter = {
      moduleIdentifier: "Suite",
      type: ETestingType.Reliability,
      reliabilityTest: {
        iteration,
        plateSourceModuleIdentifier,
        plateSourceHotelNumber,
        plateSourceSlotNumber,
        selectedTarget,
      },
    };

    const response = await TestingApi.doTestingUseFetch(payload);
    if (response.resultCode === EResultCode.Fail) {
      setIsModalErrorOpen(true);
    }
  };


  return (
    <>
      <MtiModalIllustration
        title="PSR Testing"
        icon={<SvgError />}
        message="Failed to get plate for Reliability Test"
        description="System couldn't find plate in Plate Source location"
        buttons={[
          {
            onClick: () => setIsModalErrorOpen(false),
            children: "Close",
          },
        ]}
        open={isModalErrorOpen}
        onCancel={() => setIsModalErrorOpen(false)}
      />
      <div className="testing-content">
        <div className={styles.reliabilityTestFormCard}>
          {/* TEST FREQUENCY */}
          <div className={styles.inputLineContainer}>
            <label>Test frequency</label>
            <div> : </div>
            <div className={styles.inputLineRightContainer}>
              <SpinnerTextBox
                min={0}
                max={100000}
                value={form.iteration}
                onValueChanged={(value) =>
                  setForm((prev) => ({ ...prev, iteration: value }))
                }
                style={{ height: 24, width: "100%" }}
                disabled={isDisabled}
              />
            </div>
          </div>

          {/* PLATE SOURCE */}
          <div className={styles.inputLineContainer}>
            <label>Plate source </label>
            <div> : </div>
            <div className={styles.inputLineRightContainer}>
              <CommonDropDown
                id="hotel-plate-source"
                value={form.plateSource.hotel}
                onChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    plateSource: { ...prev.plateSource, hotel: value, slot: 1 },
                  }))
                }
                dropDownDisplayValues={plateSourceHotelDropdown}
                disabled={isDisabled}
                style={{ height: 24, width: "100%" }}
              />
              <label>Slot</label>
              <div style={{ width: "50%" }}>
                <CommonDropDown
                  id="slot-plate-source"
                  value={form.plateSource.slot}
                  onChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      plateSource: { ...prev.plateSource, slot: value },
                    }))
                  }
                  dropDownDisplayValues={plateSourceSlotDropdown}
                  style={{ height: 24, width: "100%" }}
                  disabled={isDisabled}
                />
              </div>
            </div>
          </div>
        </div>

        {/*  */}
        <div className={styles.reliabilityTestFormBorderedCard}>
          <div className="testing-input-line item-3">
            <label>Hotel</label>
            <div style={{ marginRight: 5 }}> : </div>
            <MultipleSelectDropDown
              dropDownDisplayValues={groupedDisplayValues}
              id="reliability-test-hotel-drop-down"
              values={form.targetHotels}
              onChange={(value) => {
                setForm((prev) => ({
                  ...prev,
                  targetHotels: value,
                }));
              }}
              style={{ height: 24, width: "100%", fontSize: 14 }}
              colorVariant="alternate"
              disabled={isDisabled}
            />
          </div>
          <div className={styles.slotTargetContainer}>
            <label style={{ minWidth: 148 }}>Slot </label>
            <div style={{ marginRight: 5 }}> : </div>
            <div className={styles.slotTargetRightContainer}>
              <CommonDropDown
                id="slot-target"
                value={targetSlot}
                onChange={(value) => {
                  setTargetSlot(value);
                  setForm((prev) => ({
                    ...prev,
                    selectedSlotsType: [],
                    targetSlots: [],
                  }));
                }}
                dropDownDisplayValues={slotTargetDropdown}
                disabled={isDisabled || !form.targetHotels.length}
                style={{ height: 24, width: "100%" }}
              />

              {targetSlot === slotTargetValue.allSlot ? (
                <CommonDropDown
                  id="slot-target"
                  value={"All"}
                  onChange={() => { }}
                  dropDownDisplayValues={[
                    { value: "All", display: "All", disabled: true },
                  ]}
                  style={{ height: 24, width: "100%" }}
                  disabled
                />
              ) : null}

              {targetSlot === slotTargetValue.customSlot ? (
                <MultipleSelectDropDown
                  id="reliability-test-hotel-drop-down"
                  values={form.targetSlots}
                  dropDownDisplayValues={customSlotDropdown}
                  onChange={(value) =>
                    setForm((prev) => ({ ...prev, targetSlots: value }))
                  }
                  style={{ height: 24, width: "100%", fontSize: 14 }}
                  colorVariant="alternate"
                  disabled={isDisabled}
                />
              ) : null}

              {targetSlot === slotTargetValue.selectedSlot ? (
                <div className={styles.slotTargetRightContainerCheckbox}>
                  <CheckBox
                    id={`reliability-test-checkbox-${ETestingSlotSelection.Top}`}
                    name={`reliability-test-checkbox-${ETestingSlotSelection.Top}`}
                    checked={form.testingSlotSelection.includes(
                      ETestingSlotSelection.Top
                    )}
                    label="T"
                    onChange={(id) => handleCustomSlotCheckbox(id)}
                    colorVariant="alternate"
                    size="small"
                    disabled={isDisabled}
                    style={{
                      fontSize: 14,
                    }}
                  />
                  <CheckBox
                    id={`reliability-test-checkbox-${ETestingSlotSelection.Middle}`}
                    name={`reliability-test-checkbox-${ETestingSlotSelection.Middle}`}
                    checked={form.testingSlotSelection.includes(
                      ETestingSlotSelection.Middle
                    )}
                    label="M"
                    onChange={(id) => handleCustomSlotCheckbox(id)}
                    colorVariant="alternate"
                    size="small"
                    disabled={isDisabled}
                    style={{
                      fontSize: 14,
                    }}
                  />
                  <CheckBox
                    id={`reliability-test-checkbox-${ETestingSlotSelection.Bottom}`}
                    name={`reliability-test-checkbox-${ETestingSlotSelection.Bottom}`}
                    checked={form.testingSlotSelection.includes(
                      ETestingSlotSelection.Bottom
                    )}
                    label="B"
                    onChange={(id) => handleCustomSlotCheckbox(id)}
                    colorVariant="alternate"
                    size="small"
                    disabled={isDisabled}
                    style={{
                      fontSize: 14,
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="testing-button-group">
          <ToolsContentButton
            text="Stop"
            onClick={stopTest}
            disabled={!stopEnabled}
            variant="primary"
            colorVariant="black"
          />
          <ToolsContentButton
            text="run"
            onClick={runTest}
            disabled={!isRunEnabled()}
            variant="primary"
            colorVariant="default"
          />
        </div>
        <div
          className={"testing-status-box"}
          style={{
            opacity: isDisabled ? 0.5 : 1,
            overflow: "auto",
          }}
        >
          <p>{"Counter: " + testingState.reliability?.counter}</p>
        </div>
      </div>
    </>
  );
}
